import { useCallback, useEffect, useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import clsx from 'clsx';
import { AText, Box } from '@alf/uikit';
import { useABTestStore } from '@namespace/ab-test';
import { LOG_ACTIONS, useLogAmplitude } from '@namespace/analytics';
import {
  useGeneralRoutes,
  useMobileNavMenuSettings,
  useOtherSettings
} from '@namespace/cms';
import { POPUP_HALF_SCREEN, POPUP_TYPES } from '@namespace/helpers';
import { useI18n, useLocation } from '@namespace/i18n';
import {
  useAddSearchParams,
  useHistory,
  useSearchParams
} from '@namespace/router';
import { MOBILE_MENU_MAX_LENGTH } from '../../constants';
import useDetectKeyboardOpen from '../../utils/useDetectKeyboardOpen';

import styles from './index.module.css';

const BottomNavigationMenu = ({
  classNames = {},
  activeTextColor = 'header_accent_color_1'
}) => {
  const {
    menuItem = '',
    activeMenuItem = '',
    customWrapper = '',
    iconStyle = '',
    itemStyle = ''
  } = classNames;
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { push } = useHistory();
  const mobileNavigation = useMobileNavMenuSettings();
  const { home, settings } = useGeneralRoutes();
  const { animationByActionNavMenuList = [] } = useOtherSettings();
  const {
    navMenu = [],
    menuItemImage,
    menuItemImageOpen,
    menuItemImageActive,
    menuItemTitle,
    highlightOnPages: highlightMenuPages
  } = mobileNavigation;
  const { promotions } = useGeneralRoutes();
  const { abSegments = [], isSegmentsLoaded } = useABTestStore([
    'abSegments',
    'isSegmentsLoaded'
  ]);
  const isShowAdditionalMenuItem = useMemo(
    () => isSegmentsLoaded && abSegments?.includes('ab_test_casino_item'),
    [abSegments, isSegmentsLoaded]
  );
  const navMenuFormatted = useMemo(() => {
    let filteredMenu = [...navMenu];
    const menuLength = isShowAdditionalMenuItem
      ? MOBILE_MENU_MAX_LENGTH + 1
      : MOBILE_MENU_MAX_LENGTH;
    const additionalItem = {
      image: '/minio/navigationmenu/navmenu/games.svg',
      imageActive: '/minio/navigationmenu/navmenu/games_active.svg',
      title: 'content.navmenu.casino',
      action: '/casino/',
      highlightOnPages: [
        '/casino',
        '/casino/:tag',
        '/casino/:tag/all/',
        '/casino/show-game/:tag/:tag'
      ],
      _id: '64e317db93879d70f3d95322'
    };

    if (!isShowAdditionalMenuItem) {
      filteredMenu = filteredMenu.map((item) =>
        item.action === '/games/lobby/'
          ? {
              ...item,
              image: '/minio/navigationmenu/navmenu/games.svg',
              imageActive: '/minio/navigationmenu/navmenu/games_active.svg',
              highlightOnPages: [
                '/games/lobby/',
                '/casino',
                '/casino/:tag',
                '/live-casino',
                '/live-casino/:tag',
                '/casino/:tag/all/',
                '/live-casino/:tag/all/',
                '/casino/show-game/:tag/:tag',
                '/games/lobby/:tag',
                '/instant-games',
                '/instant-games/:tag'
              ]
            }
          : item
      );
    } else {
      filteredMenu = [
        ...filteredMenu.slice(0, 3),
        additionalItem,
        ...filteredMenu.slice(3)
      ];
    }

    return filteredMenu.slice(0, menuLength);
  }, [navMenu, isShowAdditionalMenuItem]);
  const { popup } = useSearchParams();

  const isProfileMenuPopupOpen = popup === POPUP_TYPES.PROFILE_MENU;
  const isNavMenuPopupOpen = popup === POPUP_TYPES.NAV_MENU;
  const isMobilePopupMenuOpen = popup === POPUP_HALF_SCREEN.MOBILE_POPUP_MENU;
  const isPopupOpen =
    isProfileMenuPopupOpen || isMobilePopupMenuOpen || isNavMenuPopupOpen;

  const addSearchParams = useAddSearchParams();
  const isKeyboardOpen = useDetectKeyboardOpen();
  const isHighlightMenu = highlightMenuPages?.some((path) =>
    matchPath({ path, end: true }, pathname)
  );
  const menuIconActive = isHighlightMenu ? menuItemImageActive : menuItemImage;
  const logAmplitude = useLogAmplitude();

  const handleMenuClick = useCallback(
    (action) => {
      switch (action) {
        case POPUP_TYPES.PROFILE_MENU:
          if (isProfileMenuPopupOpen) {
            const isSettingsPage = Boolean(
              matchPath({ path: settings, end: true }, pathname)
            );
            push(isSettingsPage ? home : pathname);
          } else {
            addSearchParams(
              { popup: POPUP_TYPES.PROFILE_MENU, from: POPUP_TYPES.NAV_MENU },
              null,
              true
            );
          }
          break;
        case POPUP_HALF_SCREEN.MOBILE_POPUP_MENU:
          if (isMobilePopupMenuOpen) {
            push(pathname);
          } else {
            addSearchParams(
              { popup: POPUP_HALF_SCREEN.MOBILE_POPUP_MENU },
              null,
              true
            );
          }
          break;
        case POPUP_TYPES.NAV_MENU:
          if (isNavMenuPopupOpen) {
            push(pathname);
          } else {
            addSearchParams({ popup: POPUP_TYPES.NAV_MENU }, null, true);
          }
          break;
        default:
          logAmplitude({
            actionName: LOG_ACTIONS.SECTION_OPEN,
            attributes: { entry_point: 'nav_menu', section_open: action },
            isMatchPath: false,
            isMatchSection: true,
            additionalRoutes: { promotions }
          });
          push(action);
          break;
      }
      return null;
    },
    [
      logAmplitude,
      addSearchParams,
      isProfileMenuPopupOpen,
      isNavMenuPopupOpen,
      isMobilePopupMenuOpen,
      pathname,
      push
    ]
  );

  useEffect(() => {
    document.documentElement.style.setProperty('--isNavBarEnabled', '1');

    return () => {
      document.documentElement.style.removeProperty('--isNavBarEnabled');
    };
  }, []);

  if (isKeyboardOpen) {
    return null;
  }

  return (
    <Box
      className={clsx([styles.wrapper, customWrapper])}
      align="center"
      data-role="bottom-navigation-menu"
    >
      {navMenuFormatted.map(
        ({ action, image, imageActive, title, highlightOnPages }) => {
          const isPathMatch = highlightOnPages.some((path) =>
            matchPath({ path, end: true }, pathname)
          );

          const isActiveItem = isPopupOpen ? action === popup : isPathMatch;
          const isShowAnimation =
            !isActiveItem && animationByActionNavMenuList.includes(action);

          return (
            <Box
              data-role="bottom-navigation-menu-item"
              data-action={action}
              onClick={() => handleMenuClick(action)}
              className={clsx(
                styles.item,
                menuItem,
                isActiveItem && activeMenuItem
              )}
              key={`bottom-menu-item-${title}`}
              justify="center"
              align="center"
            >
              <Box
                className={clsx(
                  isShowAnimation && styles.animationContainer,
                  styles.animationBtn,
                  !isShowAnimation && styles.hidden
                )}
              >
                <Box direction="column" justify="center" align="center">
                  <img
                    src={isActiveItem ? imageActive : image}
                    className={clsx(styles.icon, iconStyle)}
                    alt=""
                  />
                  <AText
                    className={clsx(styles.itemText, itemStyle)}
                    color={isActiveItem ? activeTextColor : 'header_txt_2'}
                    breed="R"
                  >
                    {t(title)}
                  </AText>
                </Box>
              </Box>
            </Box>
          );
        }
      )}
      <Box
        data-role="bottom-navigation-menu-item-menu"
        onClick={() => handleMenuClick(POPUP_TYPES.PROFILE_MENU)}
        className={clsx(
          styles.item,
          menuItem,
          isProfileMenuPopupOpen && activeMenuItem
        )}
        direction="column"
        justify="center"
        align="center"
      >
        <img
          src={isProfileMenuPopupOpen ? menuItemImageOpen : menuIconActive}
          className={clsx(styles.icon, iconStyle)}
          alt=""
        />
        <AText
          className={clsx(styles.itemText, itemStyle)}
          color={
            isProfileMenuPopupOpen || isHighlightMenu
              ? activeTextColor
              : 'header_txt_2'
          }
          breed="R"
        >
          {t(menuItemTitle, 'Menu')}
        </AText>
      </Box>
    </Box>
  );
};

export default BottomNavigationMenu;
